import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Plus, Trash2 } from "lucide-react";

const CATS_ORDER = ["Essentials", "Clothing", "Gear", "Toiletries", "Documents", "Weather"];

export default function PackingList({ trip, onUpdate }) {
  const [newItem, setNewItem] = useState("");
  const [category, setCategory] = useState("Essentials");
  const [busy, setBusy] = useState(false);

  const grouped = trip.packing.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p); return acc;
  }, {});
  const total = trip.packing.length;
  const checked = trip.packing.filter(p => p.checked).length;
  const pct = total ? (checked / total) * 100 : 0;

  const add = async () => {
    if (!newItem.trim()) return;
    try { const { data } = await api.post(`/trips/${trip.trip_id}/packing`, { item: newItem.trim(), category }); onUpdate(data); setNewItem(""); }
    catch { toast.error("Failed"); }
  };
  const toggle = async (id) => { try { const { data } = await api.patch(`/trips/${trip.trip_id}/packing/${id}`); onUpdate(data); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { const { data } = await api.delete(`/trips/${trip.trip_id}/packing/${id}`); onUpdate(data); } catch { toast.error("Failed"); } };

  const generateAI = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate-packing", { destination: trip.destination, days: trip.days.length });
      await api.post(`/trips/${trip.trip_id}/save-packing`, { items: data.items });
      const { data: fresh } = await api.get(`/trips/${trip.trip_id}`);
      onUpdate(fresh);
      toast.success("AI packing list added");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display text-2xl font-semibold">Packing list</h2>
        <Button size="sm" onClick={generateAI} disabled={busy} className="bg-[#C85A32] hover:bg-[#B04B26] text-white" data-testid="ai-packing-button">
          <Sparkles size={14} className="mr-1"/> {busy ? "Generating…" : "AI suggest"}
        </Button>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono-code text-xs text-stone-500 uppercase tracking-widest">Progress</div>
          <div className="font-mono-code text-sm">{checked}/{total}</div>
        </div>
        <Progress value={pct} className="h-2 bg-stone-100"/>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-5 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <label className="font-mono-code text-[10px] uppercase text-stone-500">Item</label>
          <Input value={newItem} onChange={(e)=>setNewItem(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&add()} placeholder="Universal adapter" data-testid="packing-item-input" className="mt-1"/>
        </div>
        <div>
          <label className="font-mono-code text-[10px] uppercase text-stone-500">Category</label>
          <select value={category} onChange={(e)=>setCategory(e.target.value)} data-testid="packing-category-select" className="mt-1 h-10 rounded-md border border-stone-300 bg-white px-3 text-sm">
            {CATS_ORDER.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Button onClick={add} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="packing-add-button"><Plus size={14} className="mr-1"/> Add</Button>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="text-center py-10 border border-dashed border-stone-300 rounded-2xl bg-white text-stone-500">No items yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {CATS_ORDER.filter(c => grouped[c]).map(cat => (
            <div key={cat} className="rounded-2xl border border-stone-200 bg-white p-5">
              <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-700 mb-3">{cat}</div>
              <ul className="space-y-2">
                {grouped[cat].map(it => (
                  <li key={it.id} className="flex items-center gap-2 group" data-testid={`packing-${it.id}`}>
                    <Checkbox checked={it.checked} onCheckedChange={()=>toggle(it.id)} data-testid={`packing-toggle-${it.id}`}/>
                    <span className={`flex-1 text-sm ${it.checked ? "line-through text-stone-400" : "text-stone-800"}`}>{it.item}</span>
                    <button onClick={()=>del(it.id)} data-testid={`packing-delete-${it.id}`} className="text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
